// SEO utility functions for business profiles

/**
 * Validates a rating value for SEO purposes
 * @param {number|string} rating - The rating to validate
 * @returns {number|null} - Valid rating between 1-5 or null if invalid
 */
export const validateRating = (rating) => {
    const numRating = Number(rating);
    if (isNaN(numRating) || numRating <= 0) return null;
    return Math.min(Math.max(numRating, 1), 5);
  };
  
  /**
   * Validates a review count for SEO purposes
   * @param {number|string} count - The review count to validate
   * @returns {number|null} - Valid review count or null if invalid
   */
  export const validateReviewCount = (count) => {
    const numCount = Number(count);
    return (!isNaN(numCount) && numCount > 0) ? numCount : null;
  };
  
  /**
   * Checks if profile data has valid SEO data for structured markup
   * @param {Object} profileData - The business profile data
   * @returns {boolean} - True if has valid rating and review count
   */
  export const hasValidSEOData = (profileData) => {
    if (!profileData) return false;
    const validRating = validateRating(profileData.avgSkillRating_all);
    const validReviewCount = validateReviewCount(profileData.reviews?.length);
    return validRating && validReviewCount;
  };
  
  /**
   * Generates canonical URL for business profile
   * @param {Object} profileData - The business profile data
   * @returns {string} - Canonical URL
   */
  export const generateCanonicalUrl = (profileData) => {
    if (!profileData || !profileData.businessName || !profileData._id) return '';
    
    // Create SEO-friendly URL slug from business name
    const businessSlug = profileData.businessName
      .toLowerCase()      
      .replace(/\s+/g, '-'); // Replace spaces with hyphens
    
    // Always use the same canonical structure regardless of how user arrived
    return `https://www.revfer.com/business/${profileData._id}/${businessSlug}`;
  };
  
  /**
   * Generates LocalBusiness structured data schema
   * @param {Object} profileData - The business profile data
   * @returns {Object|null} - LocalBusiness schema object or null
   */
  export const generateBusinessSchema = (profileData) => {
    if (!profileData) return null;

    const categories = (Array.isArray(profileData.category)
      ? profileData.category
      : [profileData.category]
    ).filter((c) => c && c !== 'provider');

    const publicTags = (profileData.tags || [])
      .filter((t) => typeof t === 'string' && !/^[A-Z0-9_]+$/.test(t));

    const businessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": generateCanonicalUrl(profileData),
      "name": profileData.businessName,
      "description": profileData.businessDescription ||
        `${profileData.businessName}${categories.length ? ' - ' + categories.join(', ') : ''}`,
      "url": generateCanonicalUrl(profileData),
      ...(profileData.telephone && { "telephone": profileData.telephone }),
      ...(profileData.priceRange && { "priceRange": profileData.priceRange }),
      ...(profileData.geocodeResults?.formattedAddress && {
        "address": {
          "@type": "PostalAddress",
          "streetAddress": profileData.geocodeResults.formattedAddress.split(',')[0],
          ...(profileData.geocodeResults.city && { "addressLocality": profileData.geocodeResults.city }),
          ...(profileData.geocodeResults.state && { "addressRegion": profileData.geocodeResults.state }),
          ...(profileData.geocodeResults.zipcode && { "postalCode": profileData.geocodeResults.zipcode }),
          "addressCountry": profileData.countryCode || "US"
        }
      }),
      ...(profileData.geocodeResults?.location && {
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": profileData.geocodeResults.location.lat,
          "longitude": profileData.geocodeResults.location.lng
        }
      }),
      ...(profileData.photoUrl && {
        "image": profileData.photoUrl,
        "logo": profileData.photoUrl
      }),
      "keywords": [
        profileData.businessName,
        ...categories,
        ...publicTags,
        "local business",
        "reviews",
        "recommendations"
      ].filter(Boolean).join(", ")
    };

    // aggregateRating/reviews block: keep your existing hasValidSEOData logic as-is
    if (hasValidSEOData(profileData)) {
      // ... unchanged ...
    }

    return businessSchema;
  };
  
  /**
   * Generates FAQ structured data schema
   * @param {Object} profileData - The business profile data
   * @returns {Object|null} - FAQ schema object or null
   */
  export const generateFAQSchema = (profileData) => {
    if (!profileData || !profileData.reviews || profileData.reviews.length === 0) return null;
  
    // Generate common FAQs based on business data
    const faqs = [
      {
        question: `What are the hours for ${profileData.businessName}?`,
        answer: profileData.businessHours || "Please contact the business directly for current hours."
      },
      {
        question: `Where is ${profileData.businessName} located?`,
        answer: profileData.geocodeResults?.formattedAddress || "Please contact the business for location details."
      },
      {
        question: `How is ${profileData.businessName} rated?`,
        answer: `${profileData.businessName} has an average rating of ${Math.round(profileData.avgSkillRating_all * 10) / 10} stars out of 5, based on ${profileData.reviews.length} customer reviews.`
      }
    ];
  
    // Add business-specific FAQ if business concept exists
    if (profileData.businessConcept) {
      faqs.push({
        question: `What type of business is ${profileData.businessName}?`,
        answer: profileData.businessConcept
      });
    }
  
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  };
  
  /**
   * Gets validated review microdata for individual reviews
   * @param {Object} review - The review object
   * @returns {Object} - Object with microdata validation info
   */
  export const getValidatedReviewMicrodata = (review) => {
    const validRating = validateRating(review.rating);
    
    if (!validRating) {
      // Return props for visible display but NO microdata
      return {
        showMicrodata: false,
        displayRating: review.rating || 0
      };
    }
  
    return {
      showMicrodata: true,
      validRating: validRating,
      displayRating: review.rating
    };
  };
  
  /**
   * Generates page title for business profile
   * @param {Object} profileData - The business profile data
   * @returns {string} - SEO-optimized page title
   */
  export const generatePageTitle = (profileData) => {
    const rawCategory = Array.isArray(profileData.category)
      ? profileData.category[0]
      : profileData.category;
    const category = rawCategory && rawCategory !== 'provider'
      ? rawCategory
      : 'Local Business';
    return `${profileData.businessName} - ${category} | Revfer`;
  };
  
  /**
   * Generates page description for business profile
   * @param {Object} profileData - The business profile data
   * @returns {string} - SEO-optimized page description
   */
  export const generatePageDescription = (profileData) => {
    const city = profileData.geocodeResults?.city || 'your area';
    const reviewCount = profileData.reviews?.length || 0;
    const rating = profileData.avgSkillRating_all
      ? Math.round(profileData.avgSkillRating_all * 10) / 10
      : null;

    const ratingPart = rating && reviewCount > 0
      ? `Rated ${rating} stars from ${reviewCount} customer review${reviewCount === 1 ? '' : 's'}. `
      : '';

    const descPart = profileData.businessDescription
      || `Find authentic reviews and recommendations for ${profileData.businessName}.`;

    const full = `${profileData.businessName} in ${city}. ${ratingPart}${descPart}`;
    // Meta descriptions should be ~155 chars; truncate at a word boundary
    return full.length <= 155
      ? full
      : full.slice(0, 154).replace(/\s+\S*$/, '') + '…';
  };
  
  /**
   * Generates page keywords for business profile
   * @param {Object} profileData - The business profile data
   * @returns {string} - SEO-optimized keywords string
   */
  export const generatePageKeywords = (profileData) => {
    const categories = (Array.isArray(profileData.category)
      ? profileData.category
      : [profileData.category]
    ).filter((c) => c && c !== 'provider');

    const publicTags = (profileData.tags || [])
      .filter((t) => typeof t === 'string' && !/^[A-Z0-9_]+$/.test(t));

    return [
      profileData.businessName,
      ...categories,
      ...publicTags,
      profileData.geocodeResults?.city,
      profileData.geocodeResults?.state,
      "local business",
      "reviews",
      "recommendations",
      "customer reviews"
    ].filter(Boolean).join(", ");
  };